import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import { useCart } from '../../hooks/useCart';
import { getProductImage } from '../../utils/imageStorage';

interface ProductStock {
  id: string;
  name: string;
  quantity: number;
  threshold: number;
  category: string;
  collection: string;
  message?: string;
}

const ProduitBonhommeNeigeLumineux: React.FC = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [stock, setStock] = useState<ProductStock | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const productId = 'bonhomme-neige-lumineux';
  const defaultImage = 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/53d762511ac970f9fd94e51d54770e93.png';
  const [productImage, setProductImage] = useState<string>(defaultImage);

  const productInfo = {
    id: 'bonhomme-neige-lumineux',
    name: 'Bonhomme de Neige Lumineux – Livre Plié',
    price: 30,
    image: defaultImage,
    category: 'Bonhommes de neige',
    collection: 'Noël'
  };

  useEffect(() => {
    const loadImage = async () => {
      const savedImage = await getProductImage(productId);
      if (savedImage) {
        setProductImage(savedImage);
      }
    };
    loadImage();
  }, []);

  useEffect(() => {
    fetchStock();
  }, []);

  const fetchStock = async () => {
    try {
      const response = await fetch('/api/inventory-management', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'get',
          productId: productInfo.id
        })
      });

      if (response.ok) {
        const data = await response.json();
        setStock(data);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du stock:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStockDisplay = () => {
    if (isLoading) return '';
    if (!stock) return '✅ En stock';
    
    const quantity = stock.quantity;
    
    if (quantity === 0) return '⛔ Rupture de stock';
    if (quantity === 1) return '💫 Plus qu\'1 pièce disponible';
    if (quantity >= 2 && quantity <= 5) return `✨ Il reste ${quantity} pièces`;
    return '✅ En stock';
  };

  const isOutOfStock = () => {
    return stock && stock.quantity === 0;
  };

  const handleAddToCart = async () => {
    if (isOutOfStock()) return;

    try {
      addToCart({
        id: productInfo.id,
        name: productInfo.name,
        price: productInfo.price,
        image: productImage,
        quantity: 1
      });

      // Mettre à jour le stock
      if (stock) {
        await fetch('/api/inventory-management', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'update',
            productId: productInfo.id,
            quantity: stock.quantity - 1
          })
        });
        
        setStock(prev => prev ? { ...prev, quantity: prev.quantity - 1 } : null);
      }

      navigate('/panier');
    } catch (error) {
      console.error('Erreur lors de l\'ajout au panier:', error);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8EDEA]">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="text-sm text-[#5C3A2E] mb-8">
          <span 
            onClick={() => navigate('/')} 
            className="hover:text-[#B97A72] cursor-pointer"
          >
            Accueil
          </span>
          <span className="mx-2">/</span>
          <span 
            onClick={() => navigate('/boutique')} 
            className="hover:text-[#B97A72] cursor-pointer"
          >
            Boutique
          </span>
          <span className="mx-2">/</span>
          <span 
            onClick={() => navigate('/noel')} 
            className="hover:text-[#B97A72] cursor-pointer"
          >
            Collection Noël
          </span>
          <span className="mx-2">/</span>
          <span className="text-[#5C3A2E]">Bonhommes de neige</span>
        </nav>

        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12">
              {/* Image du produit */}
              <div>
                <div className="w-full h-[500px] bg-white rounded-lg overflow-hidden shadow-lg mb-4">
                  <img
                    src={productImage}
                    alt="Bonhomme de Neige Lumineux – Livre Plié"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              {/* Informations du produit */}
              <div className="space-y-6">
                <div>
                  <h1 className="text-3xl font-bold text-[#5C3A2E] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {productInfo.name}
                  </h1>
                  <p className="text-sm text-[#5C3A2E] mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Collection {productInfo.collection} • {productInfo.category}
                  </p>
                  <div className="text-2xl font-medium text-[#5C3A2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {productInfo.price} €
                  </div>
                  
                  {/* Affichage du stock */}
                  <div className="text-lg font-medium mb-6 text-[#5C3A2E]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {getStockDisplay()}
                  </div>
                </div>

                {/* Description */}
                <div className="prose prose-gray max-w-none">
                  <p className="text-[#5C3A2E] leading-relaxed mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Découvrez ce bonhomme de neige lumineux entièrement réalisé à partir d'un livre plié.
                  </p>
                  <p className="text-[#5C3A2E] leading-relaxed mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Chaque pièce est unique et apporte douceur, poésie et lumière à la décoration d'hiver.
                  </p>
                  <p className="text-[#5C3A2E] leading-relaxed mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Le bonhomme de neige est décoré avec une guirlande LED intégrée, une écharpe décorative, un bonnet d'hiver et des boutons enneigés ❄️✨
                  </p>

                  <div className="bg-[#F4EDE2] rounded-lg p-4 mb-6">
                    <h3 className="font-semibold text-[#5C3A2E] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>✨ Caractéristiques :</h3>
                    <ul className="space-y-2 text-[#5C3A2E]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      <li>• Hauteur : 30 cm</li>
                      <li>• Livre plié à la main</li>
                      <li>• Guirlande LED intégrée (pile incluse)</li>
                      <li>• Matériaux : pages de livre recyclé + accessoires décoratifs</li>
                      <li>• Idéal pour décorer une entrée, une bibliothèque ou offrir un cadeau original</li>
                      <li>• Chaque modèle peut varier légèrement selon le livre utilisé</li>
                    </ul>
                  </div>

                  <div className="bg-[#F4EDE2] rounded-lg p-4 mb-6">
                    <h3 className="font-semibold text-[#5C3A2E] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>📦 Livraison :</h3>
                    <ul className="space-y-2 text-[#5C3A2E]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      <li>• Expédition sous 2 à 3 jours ouvrés (si en stock)</li>
                      <li>• Fabrication sous 5 à 7 jours (si sur commande)</li>
                      <li>• Envoi via Mondial Relay (4,20 €)</li>
                      <li>• Livraison offerte à partir de 15 € d'achat</li>
                    </ul>
                  </div>
                </div>

                {/* Bouton d'ajout au panier */}
                <button
                  onClick={handleAddToCart}
                  disabled={isOutOfStock()}
                  className={`w-full py-4 px-6 rounded-full font-semibold text-lg transition-all duration-300 whitespace-nowrap ${
                    isOutOfStock()
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-[#F4EDE2] hover:bg-[#F4EDE2]/80 text-[#5C3A2E] shadow-lg hover:shadow-xl transform hover:-translate-y-1'
                  }`}
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {isOutOfStock() ? 'Rupture de stock' : 'Ajouter au panier'}
                </button>

                {/* Informations supplémentaires */}
                <div className="text-sm text-[#5C3A2E] space-y-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <p>🎨 Création artisanale unique</p>
                  <p>♻️ Livre recyclé et upcyclé</p>
                  <p>💡 Guirlande LED intégrée</p>
                  <p>🇫🇷 Fabriqué en Provence</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ProduitBonhommeNeigeLumineux;
